import {
	Alert,
	AlertIcon,
	Badge,
	Box,
	Button,
	Flex,
	FormControl,
	FormHelperText,
	FormLabel,
	Icon,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Select,
	Spinner,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	useColorModeValue,
	useDisclosure
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { MdVpnKey, MdContentCopy, MdDelete, MdEdit } from 'react-icons/md';
import Card from '../../components/card/Card';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { RootState } from '../../store/store';
import {
	clearCreatedKey,
	createApiKey,
	editApiKeyPrice,
	fetchApiKeys,
	revokeApiKey
} from '../../store/features/apiKey/apiKey.slice';
import { fetchUsers } from '../../store/features/user/user.slice';
import { ApiKey } from '../../models/apiKey.model';
import { toastService } from '../../services/toast.service';

function formatBRL(value?: number | null) {
	if (value === undefined || value === null) return null;
	return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function ApiKeys() {
	const dispatch = useAppDispatch();
	const { list, loading, createdKey } = useAppSelector((state: RootState) => state.apiKey);
	const users = useAppSelector((state: RootState) => state.user.list);
	const createModal = useDisclosure();
	const priceModal = useDisclosure();
	const textColor = useColorModeValue('navy.700', 'white');
	const subColor = useColorModeValue('secondaryGray.700', 'secondaryGray.500');

	const [form, setForm] = useState<{ userId: string; name: string; rateLimit: string; price: string }>({
		userId: '',
		name: '',
		rateLimit: '',
		price: ''
	});
	const [selected, setSelected] = useState<ApiKey | null>(null);
	const [priceValue, setPriceValue] = useState('');

	useEffect(() => {
		dispatch(fetchApiKeys());
		dispatch(fetchUsers());
	}, [dispatch]);

	const handleCreate = async () => {
		if (!form.userId) {
			toastService.warning('Selecione o usuário dono da chave');
			return;
		}
		if (!form.name) {
			toastService.warning('Informe um nome para a chave');
			return;
		}
		const price = form.price === '' ? undefined : Number(form.price);
		if (price !== undefined && (Number.isNaN(price) || price < 0)) {
			toastService.warning('O custo por consulta não pode ser negativo');
			return;
		}
		await dispatch(
			createApiKey({
				userId: form.userId,
				data: {
					name: form.name,
					rateLimitPerHour: form.rateLimit === '' ? undefined : Number(form.rateLimit),
					pricePerQuery: price
				}
			})
		);
		setForm({ userId: '', name: '', rateLimit: '', price: '' });
		createModal.onClose();
	};

	const openPrice = (key: ApiKey) => {
		setSelected(key);
		setPriceValue(key.pricePerQuery === undefined || key.pricePerQuery === null ? '' : String(key.pricePerQuery));
		priceModal.onOpen();
	};

	const handlePrice = async () => {
		if (!selected) return;
		const price = priceValue === '' ? null : Number(priceValue);
		if (price !== null && (Number.isNaN(price) || price < 0)) {
			toastService.warning('O custo por consulta não pode ser negativo');
			return;
		}
		await dispatch(editApiKeyPrice({ id: selected.id, pricePerQuery: price }));
		priceModal.onClose();
	};

	const handleRevoke = (id: string) => dispatch(revokeApiKey({ id }));

	const copyKey = (key?: string) => {
		if (key) {
			navigator.clipboard.writeText(key);
			toastService.success('Chave copiada');
		}
	};

	return (
		<Box pt='10px'>
			{createdKey?.key && (
				<Alert status='success' borderRadius='16px' mb='20px' flexDirection='column' alignItems='flex-start'>
					<Flex align='center' w='100%'>
						<AlertIcon />
						<Text fontWeight='700'>Copie a nova API Key. Ela não será exibida novamente.</Text>
					</Flex>
					<Flex mt='10px' gap='8px' w='100%'>
						<Input value={createdKey.key} isReadOnly bg='white' fontFamily='monospace' />
						<Button variant='brand' onClick={() => copyKey(createdKey.key)}>
							<Icon as={MdContentCopy} />
						</Button>
						<Button variant='light' onClick={() => dispatch(clearCreatedKey())}>
							OK
						</Button>
					</Flex>
				</Alert>
			)}

			<Card>
				<Flex justify='space-between' align='center' mb='16px'>
					<Text fontWeight='800' fontSize='lg' color={textColor}>
						API Keys
					</Text>
					<Button variant='brand' leftIcon={<Icon as={MdVpnKey} />} onClick={createModal.onOpen}>
						Nova chave
					</Button>
				</Flex>

				{loading && list.length === 0 && <Spinner color='brand.500' />}
				{!loading && list.length === 0 && (
					<Text color={subColor} fontSize='sm'>
						Nenhuma API Key criada ainda.
					</Text>
				)}

				{list.length > 0 && (
					<Box overflowX='auto'>
						<Table variant='simple' size='sm'>
							<Thead>
								<Tr>
									<Th>Nome</Th>
									<Th>Cliente (dono)</Th>
									<Th>Chave</Th>
									<Th isNumeric>Custo/consulta</Th>
									<Th isNumeric>Limite/h</Th>
									<Th>Última uso</Th>
									<Th>Status</Th>
									<Th>Ações</Th>
								</Tr>
							</Thead>
							<Tbody>
								{list.map(key => {
									const price = formatBRL(key.pricePerQuery);
									return (
										<Tr key={key.id}>
											<Td fontWeight='600' color={textColor}>
												{key.name}
											</Td>
											<Td color={subColor}>
												{key.user ? (
													<Box>
														<Text color={textColor} fontWeight='600'>
															{key.user.name}
														</Text>
														<Text fontSize='xs'>{key.user.email}</Text>
													</Box>
												) : (
													'—'
												)}
											</Td>
											<Td color={subColor} fontFamily='monospace' fontSize='xs'>
												{key.prefix ? `${key.prefix}••••••••` : '••••••••'}
											</Td>
											<Td isNumeric color={textColor}>
												{price ?? (
													<Text as='span' color={subColor} fontSize='xs'>
														usa do usuário
													</Text>
												)}
											</Td>
											<Td isNumeric color={textColor}>
												{key.rateLimitPerHour ?? '—'}
											</Td>
											<Td color={subColor} fontSize='xs'>
												{key.lastUsedAt ? format(new Date(key.lastUsedAt), 'dd/MM/yyyy') : 'nunca'}
											</Td>
											<Td>
												<Badge colorScheme={key.active ? 'green' : 'red'} borderRadius='8px'>
													{key.active ? 'Ativa' : 'Inativa'}
												</Badge>
											</Td>
											<Td>
												<Flex gap='6px'>
													<Button size='xs' variant='light' onClick={() => openPrice(key)}>
														<Icon as={MdEdit} />
													</Button>
													<Button size='xs' variant='light' color='red.500' onClick={() => handleRevoke(key.id)}>
														<Icon as={MdDelete} />
													</Button>
												</Flex>
											</Td>
										</Tr>
									);
								})}
							</Tbody>
						</Table>
					</Box>
				)}
			</Card>

			{/* Create key modal */}
			<Modal isOpen={createModal.isOpen} onClose={createModal.onClose} isCentered>
				<ModalOverlay />
				<ModalContent borderRadius='20px'>
					<ModalHeader>Nova API Key</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<FormControl mb='16px'>
							<FormLabel fontSize='sm' fontWeight='600'>
								Usuário dono (cliente)
							</FormLabel>
							<Select
								variant='main'
								placeholder='Selecione o usuário'
								value={form.userId}
								onChange={e => setForm({ ...form, userId: e.target.value })}>
								{users.map(u => (
									<option key={u.id} value={u.id}>
										{u.name} — {u.email}
									</option>
								))}
							</Select>
							<FormHelperText fontSize='xs'>É o saldo desse usuário que será debitado no consumo via API.</FormHelperText>
						</FormControl>
						<FormControl mb='16px'>
							<FormLabel fontSize='sm' fontWeight='600'>
								Nome
							</FormLabel>
							<Input
								variant='main'
								placeholder='Ex: Integração ERP'
								value={form.name}
								onChange={e => setForm({ ...form, name: e.target.value })}
							/>
						</FormControl>
						<FormControl mb='16px'>
							<FormLabel fontSize='sm' fontWeight='600'>
								Custo por consulta (R$) — opcional
							</FormLabel>
							<Input
								variant='main'
								inputMode='decimal'
								placeholder='Vazio = usa o preço do usuário'
								value={form.price}
								onChange={e => {
									const v = e.target.value;
									if (v === '' || /^\d*\.?\d*$/.test(v)) setForm({ ...form, price: v });
								}}
							/>
						</FormControl>
						<FormControl>
							<FormLabel fontSize='sm' fontWeight='600'>
								Limite por hora (opcional)
							</FormLabel>
							<Input
								variant='main'
								type='number'
								placeholder='1000'
								value={form.rateLimit}
								onChange={e => setForm({ ...form, rateLimit: e.target.value })}
							/>
						</FormControl>
					</ModalBody>
					<ModalFooter gap='10px'>
						<Button variant='light' onClick={createModal.onClose}>
							Cancelar
						</Button>
						<Button variant='brand' onClick={handleCreate}>
							Criar
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Edit price modal */}
			<Modal isOpen={priceModal.isOpen} onClose={priceModal.onClose} isCentered>
				<ModalOverlay />
				<ModalContent borderRadius='20px'>
					<ModalHeader>Custo por consulta — {selected?.name}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<FormControl>
							<FormLabel fontSize='sm'>Custo por consulta (R$)</FormLabel>
							<Input
								variant='main'
								inputMode='decimal'
								placeholder='Vazio = usa o preço do usuário'
								value={priceValue}
								onChange={e => {
									const v = e.target.value;
									if (v === '' || /^\d*\.?\d*$/.test(v)) setPriceValue(v);
								}}
							/>
							<FormHelperText fontSize='xs'>Deixe vazio para a chave cobrar o mesmo preço do usuário dono.</FormHelperText>
						</FormControl>
					</ModalBody>
					<ModalFooter gap='10px'>
						<Button variant='light' onClick={priceModal.onClose}>
							Cancelar
						</Button>
						<Button variant='brand' onClick={handlePrice}>
							Salvar
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</Box>
	);
}
